import React, { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from './supabase';
import {
  PLAN_LIMITS, getUsageCount, checkAndIncrementUsage, getResetDate,
} from './config/planLimits';

// Full domain names — keys returned by the updated edge function
const DOMAIN_LABELS = [
  'Access Control',
  'Awareness and Training',
  'Audit and Accountability',
  'Configuration Management',
  'Identification and Authentication',
  'Incident Response',
  'Maintenance',
  'Media Protection',
  'Personnel Security',
  'Physical Protection',
  'Risk Assessment',
  'Security Assessment',
  'System and Communications Protection',
  'System and Information Integrity',
];

// Short code abbreviations shown in the pill header
const DOMAIN_CODES = {
  'Access Control':                       'AC',
  'Awareness and Training':               'AT',
  'Audit and Accountability':             'AU',
  'Configuration Management':             'CM',
  'Identification and Authentication':    'IA',
  'Incident Response':                    'IR',
  'Maintenance':                          'MA',
  'Media Protection':                     'MP',
  'Personnel Security':                   'PS',
  'Physical Protection':                  'PE',
  'Risk Assessment':                      'RA',
  'Security Assessment':                  'CA',
  'System and Communications Protection': 'SC',
  'System and Information Integrity':     'SI',
};

// Thin progress bar with amber/red thresholds
function UsageMeter({ current, limit, label }) {
  if (limit === Infinity || limit === 0) return null;
  const pct      = Math.min(100, Math.round((current / limit) * 100));
  const barColor = pct >= 95 ? 'var(--red)' : pct >= 80 ? 'var(--amber)' : 'var(--accent)';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{label}</span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: pct >= 95 ? 'var(--red)' : pct >= 80 ? 'var(--amber)' : 'var(--text2)',
        }}>
          {current} / {limit}
        </span>
      </div>
      <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: barColor, borderRadius: 2, transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// "covered" | "partial" | "missing" — from updated edge function
function domainStatus(s) {
  if (s === 'covered' || s === 'yes')   return { bg: 'var(--green-bg)', color: 'var(--green)', label: 'Covered',     icon: '✓' };
  if (s === 'partial')                  return { bg: 'var(--amber-bg)', color: 'var(--amber)', label: 'Partial',     icon: '—' };
  if (s === 'missing' || s === 'no')   return { bg: 'var(--red-bg)',   color: 'var(--red)',   label: 'Not Covered', icon: '✕' };
  return                                       { bg: 'var(--surface2)', color: 'var(--text3)', label: 'Unknown',     icon: '?' };
}

// ─── File Upload Zone ─────────────────────────────────────────────────────────

function FileUploadZone({ onFile, dragging, setDragging, selectedFile, disabled }) {
  const inputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (disabled) return;
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  }, [onFile, setDragging, disabled]);

  const handleDragOver  = useCallback((e) => { e.preventDefault(); if (!disabled) setDragging(true); }, [setDragging, disabled]);
  const handleDragLeave = useCallback(() => setDragging(false), [setDragging]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => !disabled && inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? 'var(--accent)' : selectedFile ? 'var(--green)' : 'var(--border2)'}`,
        borderRadius: 12, padding: '48px 32px', textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: dragging ? 'var(--blue-bg)' : selectedFile ? 'var(--green-bg)' : 'var(--surface)',
        transition: 'all 0.2s', opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
        disabled={disabled}
      />
      <div style={{
        marginBottom: 14, display: 'flex', justifyContent: 'center',
        color: selectedFile ? 'var(--green)' : 'var(--text3)', opacity: selectedFile ? 1 : 0.5,
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="9" y1="13" x2="15" y2="13"/>
          <line x1="9" y1="17" x2="15" y2="17"/>
        </svg>
      </div>
      {selectedFile ? (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--green)' }}>
            {selectedFile.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {selectedFile.size > 1024 * 1024
              ? `${(selectedFile.size / 1024 / 1024).toFixed(1)} MB`
              : `${Math.round(selectedFile.size / 1024)} KB`}{' — '}click to change
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Drop your SSP document here or click to browse
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>
            PDF (best results) or Word Document (.docx)
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>Maximum file size: 10 MB</div>
        </div>
      )}
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ value }) {
  return (
    <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.min(100, Math.max(0, value))}%`,
        background: 'var(--accent)', borderRadius: 3, transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

// ─── Coverage Grid (14 domain pills) ─────────────────────────────────────────

function CoverageGrid({ coverage }) {
  const statuses = DOMAIN_LABELS.map(name => coverage?.[name] || 'unknown');
  const coveredCount = statuses.filter(s => s === 'covered' || s === 'yes').length;
  const partialCount = statuses.filter(s => s === 'partial').length;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Domain Coverage</div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          {coveredCount} covered · {partialCount} partial · {14 - coveredCount - partialCount} missing
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 8,
      }}>
        {DOMAIN_LABELS.map(name => {
          const raw = coverage?.[name] || 'unknown';
          const { bg, color, icon } = domainStatus(raw);
          const code = DOMAIN_CODES[name] || '';
          return (
            <div key={name} style={{
              background: bg, border: `1px solid ${color}44`,
              borderRadius: 8, padding: '10px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color, fontFamily: 'var(--mono)' }}>{code}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, lineHeight: 1.3 }}>{name}</div>
              </div>
              <span style={{
                fontSize: 16, fontWeight: 700, color, flexShrink: 0, marginLeft: 10,
              }}>{icon}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Overall Assessment Header ────────────────────────────────────────────────

function OverallAssessmentHeader({ assessment, percentage, companyName, systemName, sprsImpact }) {
  const config = {
    Comprehensive: { bg: 'var(--green-bg)', color: 'var(--green)', icon: '✓' },
    Partial:       { bg: 'var(--amber-bg)', color: 'var(--amber)', icon: '~' },
    Incomplete:    { bg: 'var(--red-bg)',   color: 'var(--red)',   icon: '!' },
  };
  const cfg = config[assessment] || config.Incomplete;
  const sprsConfig = {
    positive: { color: 'var(--green)', bg: 'var(--green-bg)', label: 'SPRS Impact: Positive' },
    negative: { color: 'var(--red)',   bg: 'var(--red-bg)',   label: 'SPRS Impact: Negative' },
    neutral:  { color: 'var(--text3)', bg: 'var(--surface2)', label: 'SPRS Impact: Neutral'  },
  };
  const sprsCfg = sprsConfig[sprsImpact] || sprsConfig.neutral;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Overall Assessment</div>

      {/* Company / system metadata if extracted */}
      {(companyName || systemName) && (
        <div style={{
          display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap',
        }}>
          {companyName && (
            <div style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 6,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              color: 'var(--text2)',
            }}>
              <span style={{ color: 'var(--text3)' }}>Organization: </span>
              <strong>{companyName}</strong>
            </div>
          )}
          {systemName && (
            <div style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 6,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              color: 'var(--text2)',
            }}>
              <span style={{ color: 'var(--text3)' }}>System: </span>
              <strong>{systemName}</strong>
            </div>
          )}
        </div>
      )}

      <div style={{
        background: cfg.bg, border: `1px solid ${cfg.color}`,
        borderRadius: 12, padding: '22px 24px',
        display: 'flex', alignItems: 'center', gap: 20,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
          background: cfg.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700,
        }}>{cfg.icon}</div>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: cfg.color, lineHeight: 1 }}>
              {assessment || 'Unknown'}
            </div>
            {percentage != null && (
              <div style={{ fontSize: 18, fontWeight: 700, color: cfg.color, opacity: 0.8 }}>
                {percentage}% coverage
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, maxWidth: 480, marginBottom: sprsImpact ? 10 : 0 }}>
            {assessment === 'Comprehensive' && 'Your SSP documents 80%+ of required CMMC controls. You are well-positioned for assessment.'}
            {assessment === 'Partial' && 'Your SSP covers 40–79% of required controls. Significant gaps remain before assessment readiness.'}
            {(!assessment || assessment === 'Incomplete') && 'Your SSP documents fewer than 40% of required controls. Major remediation effort required.'}
          </div>
          {sprsImpact && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: sprsCfg.bg, border: `1px solid ${sprsCfg.color}44`,
              borderRadius: 6, padding: '4px 10px',
              fontSize: 11, fontWeight: 600, color: sprsCfg.color,
            }}>
              {sprsCfg.label}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Gaps Identified ──────────────────────────────────────────────────────────

function GapsSection({ gaps }) {
  if (!gaps?.length) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Gaps Identified</div>
      <div style={{
        background: 'var(--red-bg)', border: '1px solid var(--red)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        {gaps.map((gap, i) => (
          <div key={i} style={{
            padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12,
            borderBottom: i < gaps.length - 1 ? '1px solid rgba(255,80,80,0.2)' : 'none',
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: 'var(--red)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
            }}>{i + 1}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ fontSize: 12, color: 'var(--red)', lineHeight: 1.6 }}>{gap}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Recommendations ──────────────────────────────────────────────────────────

function RecommendationsSection({ recommendations }) {
  if (!recommendations?.length) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Recommendations</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {recommendations.map((rec, i) => (
          <div key={i} style={{
            background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
            borderRadius: 8, padding: '12px 16px',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: 'var(--accent)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700,
            }}>{i + 1}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
              <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{rec}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SSPReader({ session, answers, setAnswer, onApplyData, userPlan, onUpgrade }) {
  const [dragging,      setDragging]      = useState(false);
  const [file,          setFile]          = useState(null);           // File object
  const [uploading,     setUploading]     = useState(false);
  const [analyzing,     setAnalyzing]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMsg,     setStatusMsg]     = useState('');
  const [results,       setResults]       = useState(null);
  const [error,         setError]         = useState('');
  const [monthlyUsage,  setMonthlyUsage]  = useState(0);
  const progressIntervalRef               = useRef(null);

  const processing = uploading || analyzing;
  const plan   = (userPlan || 'professional').toLowerCase();
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.professional;
  const limit  = limits.document_uploads;
  const userId = session?.user?.id;

  useEffect(() => {
    if (!userId) return;
    getUsageCount(supabase, userId, 'document_upload').then(setMonthlyUsage);
  }, [userId]);

  useEffect(() => {
    return () => { if (progressIntervalRef.current) clearInterval(progressIntervalRef.current); };
  }, []);

  function startSmoothProgress(from, to, durationMs) {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    const steps     = Math.max(1, durationMs / 300);
    const increment = (to - from) / steps;
    let current     = from;
    progressIntervalRef.current = setInterval(() => {
      current += increment;
      if (current >= to) {
        current = to;
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setUploadProgress(Math.round(current));
    }, 300);
  }

  async function handleFile(selectedFile) {
    // Validate: PDF only from Edge Function perspective, but accept docx too
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!allowed.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|docx|doc)$/i)) {
      setError('Only PDF and Word documents (.pdf, .docx, .doc) are supported.');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be under 10 MB. Please compress your PDF or use a smaller document.');
      return;
    }

    // Check + increment usage atomically
    const usage = await checkAndIncrementUsage(supabase, userId, 'document_upload', userPlan);
    if (!usage.allowed) {
      setError(
        usage.reason === 'upgrade_required'
          ? 'Document Reader requires a Professional plan or higher. Upgrade to import SSPs directly into your assessment.'
          : `You have used all ${limit} document analyses for this month. Your limit resets on ${getResetDate()}. Upgrade to Business for 20 analyses/month.`
      );
      return;
    }
    setMonthlyUsage(usage.current);

    setError('');
    setResults(null);
    setFile(selectedFile);
    setUploading(true);
    setUploadProgress(5);
    setStatusMsg('Uploading document to secure storage...');

    let uploadedPath = null;

    try {
      // Phase 1: Upload to Supabase Storage
      const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${userId}/${Date.now()}_${safeName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: selectedFile.type || 'application/pdf',
        });

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);
      uploadedPath = uploadData.path;

      setUploading(false);
      setUploadProgress(40);
      setAnalyzing(true);
      setStatusMsg('Analyzing document with AI...');

      // Smooth progress 40 → 88 over ~50 seconds
      startSmoothProgress(40, 88, 50000);

      const { data, error: fnError } = await supabase.functions.invoke('analyze-document', {
        body: { filePath: uploadedPath, userId },
      });

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (fnError) throw new Error('Analysis failed: ' + fnError.message);
      if (!data?.success) throw new Error(data?.error || 'Analysis returned no results. Please try again.');

      setUploadProgress(100);
      setStatusMsg('Complete!');
      setResults(data.analysis);

    } catch (err) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    } finally {
      // Clean up file from storage after analysis (fire and forget)
      if (uploadedPath) {
        supabase.storage.from('documents').remove([uploadedPath]).catch(() => {});
      }
      setUploading(false);
      setAnalyzing(false);
      setUploadProgress(0);
      setStatusMsg('');
    }
  }

  function handleReset() {
    setError('');
    setResults(null);
    setFile(null);
    setUploadProgress(0);
  }

  function handleExport() {
    if (!results) return;
    const lines = [
      'SSP DOCUMENT ANALYSIS — TheClearAudit',
      '='.repeat(50),
      '',
      `Overall Assessment: ${results.overall_assessment || 'Unknown'}`,
      results.overall_percentage != null ? `Coverage: ${results.overall_percentage}%` : '',
      results.company_name ? `Organization: ${results.company_name}` : '',
      results.system_name  ? `System: ${results.system_name}` : '',
      '',
      'DOMAIN COVERAGE',
      '-'.repeat(30),
      ...DOMAIN_LABELS.map(name => {
        const status = results.coverage_summary?.[name] || 'unknown';
        return `${(DOMAIN_CODES[name] || '').padEnd(4)} ${name.padEnd(42)} ${status.toUpperCase()}`;
      }),
      '',
      'GAPS IDENTIFIED',
      '-'.repeat(30),
      ...(results.gaps || []).map((g, i) => `${i + 1}. ${g}`),
      '',
      'RECOMMENDATIONS',
      '-'.repeat(30),
      ...(results.recommendations || []).map((r, i) => `${i + 1}. ${r}`),
      '',
      `Generated: ${new Date().toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
      'TheClearAudit — theclearaudit.com',
    ].filter(l => l !== null);

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `TheClearAudit_SSP_Analysis_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const atLimit = limit !== Infinity && monthlyUsage >= limit;

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Disclaimer — always visible */}
      <div style={{
        marginBottom: 20, padding: '10px 14px', borderRadius: 8,
        background: 'var(--amber-bg)', border: '1px solid var(--amber)',
        fontSize: 11, color: 'var(--amber)', lineHeight: 1.5,
        display: 'flex', alignItems: 'flex-start', gap: 8,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span>
          Do not upload classified, CUI, or export-controlled documents. This tool analyzes document structure and coverage against CMMC frameworks only. No document content is stored after analysis.
        </span>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>SSP Document Reader</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 600 }}>
          Upload an existing System Security Plan (PDF or Word) and our compliance AI will analyze it against
          all 14 CMMC domains — identifying coverage gaps and providing specific recommendations.
        </div>
      </div>

      {/* Usage meter */}
      {limit !== Infinity && limit > 0 && (
        <div style={{ marginBottom: 16 }}>
          <UsageMeter current={monthlyUsage} limit={limit} label="Document Analyses this month" />
        </div>
      )}

      {/* Upgrade prompt when at limit */}
      {atLimit && (
        <div style={{
          background: limit === 0 ? 'var(--blue-bg)' : 'var(--amber-bg)',
          border: `1px solid ${limit === 0 ? 'rgba(45,125,210,0.3)' : 'rgba(245,158,11,0.3)'}`,
          borderRadius: 10, padding: '16px 18px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6,
            color: limit === 0 ? 'var(--accent)' : 'var(--amber)',
          }}>
            {limit === 0 ? 'Pro Plan Required' : 'Monthly Limit Reached'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 14 }}>
            {limit === 0
              ? 'Document Reader requires a Professional plan or higher. Upgrade to import SSPs directly into your assessment.'
              : `You have used all ${limit} document analyses for this month. Your limit resets on ${getResetDate()}. Upgrade to Business for 20 analyses/month.`}
          </div>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              style={{
                padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
              }}
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}

      {/* Upload zone — shown when not processing and no results */}
      {!processing && !results && (
        <FileUploadZone
          onFile={handleFile}
          dragging={dragging}
          setDragging={setDragging}
          selectedFile={file}
          disabled={atLimit}
        />
      )}

      {/* Processing state */}
      {processing && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 12, padding: 40, textAlign: 'center',
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px',
            background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)',
          }}>
            {uploading ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {uploading ? 'Uploading document...' : 'Analyzing document with AI...'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 22 }}>
            {statusMsg || file?.name}
          </div>
          <div style={{ maxWidth: 420, margin: '0 auto 8px' }}>
            <ProgressBar value={uploadProgress} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>{uploadProgress}%</div>
          {analyzing && (
            <div style={{ fontSize: 11, color: 'var(--text3)', opacity: 0.7 }}>
              Analyzing 14 CMMC domains — this typically takes 20–40 seconds.
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {error && !processing && (
        <div style={{
          marginTop: 16, padding: '16px 20px', borderRadius: 10,
          background: 'var(--red-bg)', border: '1px solid var(--red)',
        }}>
          <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600, marginBottom: 6 }}>
            {error.includes('timed out')                          ? 'Request Timed Out'  :
             error.includes('Upgrade') || error.includes('plan') ? 'Upgrade Required'   :
             error.includes('Network')                           ? 'Network Error'      :
             error.includes('Upload failed')                     ? 'Upload Failed'      : 'Analysis Failed'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--red)', opacity: 0.85, marginBottom: 12 }}>{error}</div>
          {!error.includes('Upgrade') && !error.includes('plan') && (
            <button
              onClick={handleReset}
              style={{
                fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--red)',
                background: 'transparent', color: 'var(--red)', fontFamily: 'var(--font)',
              }}
            >
              Try Again
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {results && !processing && (
        <div>
          {/* Success banner */}
          <div style={{
            marginBottom: 24, padding: '12px 16px', borderRadius: 8,
            background: 'var(--green-bg)', border: '1px solid var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>
                Analysis complete — {file?.name}
              </span>
            </div>
          </div>

          {/* Section 1 — Overall Assessment */}
          <OverallAssessmentHeader
            assessment={results.overall_assessment}
            percentage={results.overall_percentage}
            companyName={results.company_name}
            systemName={results.system_name}
            sprsImpact={results.estimated_sprs_impact}
          />

          {/* Section 2 — Domain Coverage Grid */}
          <CoverageGrid coverage={results.coverage_summary} />

          {/* Section 3 — Top Gaps */}
          <GapsSection gaps={results.gaps} />

          {/* Section 4 — Recommendations */}
          <RecommendationsSection recommendations={results.recommendations} />

          {/* Raw fallback if JSON parse failed */}
          {results.raw && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, padding: 16, marginBottom: 24,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--amber)' }}>
                Raw Analysis Output
              </div>
              <pre style={{
                fontSize: 11, color: 'var(--text2)', lineHeight: 1.6,
                whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'var(--mono)',
              }}>
                {results.raw}
              </pre>
            </div>
          )}

          {/* Section 5 — Actions row */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              onClick={handleReset}
              style={{
                fontSize: 12, fontWeight: 600, padding: '9px 18px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--border2)',
                background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
              }}
            >
              Analyze Another Document
            </button>
            <button
              onClick={handleExport}
              style={{
                fontSize: 12, fontWeight: 600, padding: '9px 18px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export Analysis
            </button>
          </div>
        </div>
      )}

      {/* Info panel — only shown before any upload */}
      {!processing && !results && !error && (
        <div style={{
          marginTop: 28, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px 18px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>What you'll get</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {[
              'Coverage summary for all 14 CMMC domains — Covered / Partial / Missing',
              'Top 5 critical gaps that need to be addressed before assessment',
              'Overall completeness rating: Comprehensive, Partial, or Incomplete',
              'Percentage coverage score extracted from document',
              'Top 3 actionable recommendations specific to your document',
              'PDF documents provide the highest accuracy — Word docs are supported but may vary',
            ].map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--green)', flexShrink: 0, marginTop: 1 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                {t}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
